import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AddDeviceWarrantyRequestDto,
  EcommerceShoppingCartClient,
} from "@lib/ShiOneClient";
import { useApi } from "@utils/useApi";
import { Toast } from "shared-ui/src/components/Toast";
import { invalidateCart } from "@features/ecommerce/cart/cartQueryKeys";

export interface AttachWarrantyArgs {
  baseSystemId: number;
  baseSystemOptionId: number;
  /** Device quantity; the warranty quantity is synced to this by the backend. */
  quantity: number;
}

/**
 * Attaches a selected warranty to a just-added config-system device (SPU-535 UI over
 * the SPU-533 endpoint). Exactly one warranty is attached and its quantity is matched
 * to the device quantity server-side. Invalidates the cart caches so the mini-cart and
 * full cart page reflect the attached warranty.
 */
export default function useAttachWarranty() {
  const api = useApi(EcommerceShoppingCartClient);
  const queryClient = useQueryClient();

  return useMutation<void, Error, AttachWarrantyArgs>({
    // Prices are intentionally not sent: the server re-prices the device and warranty
    // from the catalog (SPU-527 security fix), so client-supplied prices are untrusted.
    mutationFn: (args) =>
      api.addDeviceWithWarranty(
        new AddDeviceWarrantyRequestDto({
          baseSystemId: args.baseSystemId,
          baseSystemOptionId: args.baseSystemOptionId,
          quantity: args.quantity,
        }),
      ),
    onSuccess: async () => {
      await invalidateCart(queryClient);
      Toast("Warranty added to your device.");
    },
    onError: (error) => {
      Toast(`Failed to add warranty. Reason: ${error?.message}`, true, 7000);
    },
  });
}
